import { forwardRef } from 'react';
import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';

import { cn } from '../../lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<AntButtonProps, 'type' | 'size' | 'variant'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const VARIANT_TYPE: Record<ButtonVariant, AntButtonProps['type']> = {
  primary: 'primary',
  secondary: 'default',
  outline: 'default',
  ghost: 'text',
  destructive: 'primary',
  link: 'link',
};

const SIZE_MAP: Record<ButtonSize, AntButtonProps['size']> = {
  sm: 'small',
  md: 'middle',
  lg: 'large',
};

/**
 * Nút hành động (§9.3.1).
 *
 * Phơi API kiểu shadcn (`variant` / `size`) thay vì toàn bộ bề mặt của antd.
 *
 * Bán kính **để antd tự quyết**: không ghi đè, không dùng `!important`. Nhờ vậy
 * nút ở đây và mọi control khác của antd luôn cùng một độ bo góc, kể cả khi đổi
 * `borderRadius` ở `ConfigProvider`.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className, ...props },
  ref,
) {
  return (
    <AntButton
      ref={ref}
      type={VARIANT_TYPE[variant]}
      size={SIZE_MAP[size]}
      danger={variant === 'destructive' || props.danger}
      className={cn(variant === 'outline' && 'border-strong', className)}
      {...props}
    />
  );
});
